/**
 * 富文本编辑器：contentEditable + 轻量格式工具栏（加粗/斜体/下划线/删除线/
 * 标题/有序无序列表/清除格式），值以 HTML 字符串形式双向同步。
 *
 * 用于文本节点的行内编辑与放大编辑弹窗。工具栏按钮 onPointerDown 阻止焦点
 * 转移以保持选区；粘贴按纯文本插入（富文本仅来自工具栏与本编辑器内部操作）。
 */
import { useCallback, useEffect, useRef } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Heading2,
  Eraser,
} from 'lucide-react';

interface RichTextEditorProps {
  /** HTML 内容（受控） */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  /** compact 模式隐藏低频按钮（标题/清除格式） */
  compact?: boolean;
  /** 只读模式：隐藏工具栏、禁用编辑 */
  readOnly?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  style,
  compact = false,
  readOnly = false,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastSyncedRef = useRef('');

  const renderToEditor = useCallback((html: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.innerHTML = html;
    lastSyncedRef.current = html;
  }, []);

  useEffect(() => {
    if (value === lastSyncedRef.current) return;
    renderToEditor(value);
  }, [value, renderToEditor]);

  const handleInput = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const html = editor.innerHTML;
    lastSyncedRef.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand(command, false, arg);
    handleInput();
  };

  const isEmpty = !value || /^[ \t\r\n\s]*$/.test(value.replace(/<br\s*\/?>/gi, ''));

  const toolBtn =
    'grid h-6 w-6 place-items-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700';

  return (
    <div className="flex h-full flex-col gap-1">
      {/* 格式工具栏（只读模式隐藏） */}
      {!readOnly && (
        <div className="flex shrink-0 items-center gap-0.5 rounded-md border border-slate-200 bg-slate-50 px-1 py-0.5">
          <button type="button" className={toolBtn} title="加粗" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('bold')}>
            <Bold className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={toolBtn} title="斜体" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('italic')}>
            <Italic className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={toolBtn} title="下划线" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('underline')}>
            <Underline className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={toolBtn} title="删除线" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('strikeThrough')}>
            <Strikethrough className="h-3.5 w-3.5" />
          </button>
          {!compact && (
            <>
              <span className="mx-0.5 h-4 w-px bg-slate-200" />
              <button type="button" className={toolBtn} title="标题" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', '<h3>')}>
                <Heading2 className="h-3.5 w-3.5" />
              </button>
              <button type="button" className={toolBtn} title="无序列表" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')}>
                <List className="h-3.5 w-3.5" />
              </button>
              <button type="button" className={toolBtn} title="有序列表" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')}>
                <ListOrdered className="h-3.5 w-3.5" />
              </button>
              <span className="mx-0.5 h-4 w-px bg-slate-200" />
              <button type="button" className={toolBtn} title="清除格式" onPointerDown={(e) => e.preventDefault()} onClick={() => exec('removeFormat')}>
                <Eraser className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      )}

      {/* 编辑区 */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, text);
          }}
          className={`${className ?? ''} w-full overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-light-border bg-white p-2 text-sm leading-relaxed text-light-text outline-none ${readOnly ? 'cursor-default' : 'focus:border-blue-400 focus:ring-1 focus:ring-blue-400'}`}
          style={style}
        />
        {isEmpty && placeholder && (
          <div className="pointer-events-none absolute left-2 top-2 text-sm text-light-muted">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}
